import React, { useContext, useMemo } from 'react'
import { rankWith, and, scopeEndsWith } from '@jsonforms/core'
import { JsonForms } from '@jsonforms/react'
import { materialRenderers, materialCells } from '@jsonforms/material-renderers'
import { withJsonFormsControlProps } from '@jsonforms/react'

import objectPath from 'object-path'
import { DesignContext } from 'contexts/DesignContext'
import ComponentWrapper from 'designer/component/display/layout/control/ComponentWrapper'
import DragContainerWrapper from 'designer/component/system/layout/control/DragContainerWrapper'
import CustomTab from 'designer/component/container/tab/Tab'
import {
  createTabSystemRenderer,
  createTabDisplayRenderer,
} from 'designer/component/control/renderer'
import {
  getTabActiveKey,
  getTabActiveRenderKey,
  getTabElementKey,
} from 'designer/component/container/tab/TabHelper'

const tabTester = rankWith(
  3,
  and(scopeEndsWith('tab'), (schema) => !schema.subGroup)
)

const subTabTester = rankWith(
  3,
  and(scopeEndsWith('tab'), (schema) => schema.subGroup)
)

const useActiveTabKey = (uischema = {}) => {
  const { isUiChanged = {} } = useContext(DesignContext)
  const activeTab = objectPath.get(isUiChanged, ['activeTab', uischema.key])

  return getTabActiveKey(uischema, activeTab)
}

export const TabSystem = (props) => {
  let { data, schema, uischema, handleChange, path, config } = props
  const defaultActiveKey =
    uischema.elements.length > 0 ? getTabElementKey(uischema.elements[0]) : ''
  const activeTabKey = useActiveTabKey(uischema) || defaultActiveKey
  const activeRenderKey = getTabActiveRenderKey(
    uischema,
    activeTabKey,
    defaultActiveKey,
  )
  const renderers = useMemo(
    () => createTabSystemRenderer(uischema.elements, activeRenderKey),
    [JSON.stringify(uischema.elements), activeRenderKey]
  )
  const displayRenderers = useMemo(
    () => createTabDisplayRenderer(uischema.elements, activeRenderKey),
    [JSON.stringify(uischema.elements), activeRenderKey]
  )

  return (
    <DragContainerWrapper {...props}>
      <CustomTab
        {...props}
        renderers={renderers}
        displayRenderers={displayRenderers}
        activeTabKey={activeTabKey}
        isSystem={true}
      >
        <JsonForms
          schema={schema}
          uischema={uischema}
          data={data}
          renderers={materialRenderers}
          cells={materialCells}
          onChange={({ data, errors }) => {
            handleChange(path, data)
          }}
          config={config}
        />
      </CustomTab>
    </DragContainerWrapper>
  )
}

export const SubTabSystem = (props) => {
  let { data, schema, uischema, handleChange, path, config } = props
  const defaultActiveKey =
    uischema.elements.length > 0 ? getTabElementKey(uischema.elements[0]) : ''
  const activeTabKey = useActiveTabKey(uischema) || defaultActiveKey
  const activeRenderKey = getTabActiveRenderKey(
    uischema,
    activeTabKey,
    defaultActiveKey,
  )
  const renderers = useMemo(
    () => createTabSystemRenderer(uischema.elements, activeRenderKey),
    [JSON.stringify(uischema.elements), activeRenderKey]
  )

  const displayRenderers = useMemo(
    () => createTabDisplayRenderer(uischema.elements, activeRenderKey),
    [JSON.stringify(uischema.elements), activeRenderKey]
  )

  return (
    <DragContainerWrapper {...props}>
      <CustomTab
        {...props}
        renderers={renderers}
        displayRenderers={displayRenderers}
        activeTabKey={activeTabKey}
        isSystem={true}
      >
        <JsonForms
          schema={schema}
          uischema={uischema}
          data={data}
          renderers={materialRenderers}
          cells={materialCells}
          onChange={({ data, errors }) => {
            handleChange(path, data)
          }}
          config={config}
        />
      </CustomTab>
    </DragContainerWrapper>
  )
}

export const TabDisplay = (props) => {
  let { data, schema, uischema, handleChange, path, config } = props
  const elements = uischema.elements
  const defaultActiveKey =
    elements?.length > 0 ? getTabElementKey(elements[0]) : ''
  const activeTabKey = useActiveTabKey(uischema) || defaultActiveKey
  const activeRenderKey = getTabActiveRenderKey(
    uischema,
    activeTabKey,
    defaultActiveKey,
  )
  const renderers = useMemo(
    () => createTabDisplayRenderer(elements || [], activeRenderKey),
    [elements, activeRenderKey]
  )

  return (
    <ComponentWrapper {...props}>
      <CustomTab
        {...props}
        renderers={renderers}
        activeTabKey={activeTabKey}
      >
        <JsonForms
          schema={schema}
          uischema={uischema}
          data={data}
          renderers={materialRenderers}
          cells={materialCells}
          onChange={({ data, errors }) => {
            handleChange(path, data)
          }}
          config={config}
        />
      </CustomTab>
    </ComponentWrapper>
  )
}

export const SubTabDisplay = (props) => {
  let { data, schema, uischema, handleChange, path, config } = props
  const elements = uischema.elements
  const defaultActiveKey =
    elements?.length > 0 ? getTabElementKey(elements[0]) : ''
  const activeTabKey = useActiveTabKey(uischema) || defaultActiveKey
  const activeRenderKey = getTabActiveRenderKey(
    uischema,
    activeTabKey,
    defaultActiveKey,
  )
  const renderers = useMemo(
    () => createTabDisplayRenderer(elements || [], activeRenderKey),
    [elements, activeRenderKey]
  )

  return (
    <ComponentWrapper {...props}>
      <CustomTab
        {...props}
        renderers={renderers}
        activeTabKey={activeTabKey}
      >
        <JsonForms
          schema={schema}
          uischema={uischema}
          data={data}
          renderers={materialRenderers}
          cells={materialCells}
          onChange={({ data, errors }) => {
            handleChange(path, data)
          }}
          config={config}
        />
      </CustomTab>
    </ComponentWrapper>
  )
}

export const tabSystem = {
  tester: tabTester,
  renderer: withJsonFormsControlProps(TabSystem),
}

export const subTabSystem = {
  tester: subTabTester,
  renderer: withJsonFormsControlProps(SubTabSystem),
}

export const tabDisplay = {
  tester: tabTester,
  renderer: withJsonFormsControlProps(TabDisplay),
}

export const subTabDisplay = {
  tester: subTabTester,
  renderer: withJsonFormsControlProps(SubTabDisplay),
}
